import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fmtDate, today } from "@/lib/api";
import { PageHeader, EmptyState, Money } from "@/components/common";
import { OP_CATEGORIES, can } from "@/lib/labels";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inputCls = "h-10 bg-[#0F0F12] border-[#1A1A22] focus:border-blue-500 focus:ring-1 focus:ring-blue-500";
const labelCls = "text-xs font-semibold uppercase tracking-[0.15em] text-zinc-400";

export default function Operational() {
  const { user } = useAuth();
  const [costs, setCosts] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("all");
  const [month, setMonth] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (branch !== "all") params.set("branch_id", branch);
    if (month) params.set("month", month);
    api.get(`/op-costs?${params}`).then((r) => setCosts(r.data));
  }, [branch, month]);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (open) {
      const b = user?.role === "admin_cabang" ? user.branch_id : (branches[0]?.id || "");
      setForm({ branch_id: b, category: OP_CATEGORIES[0], amount: "", date: today(), description: "", cash_branch_id: b });
    }
  }, [open, branches, user]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/op-costs", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Biaya operasional dicatat");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus biaya operasional ini?")) return;
    try {
      await api.delete(`/op-costs/${id}`);
      toast.success("Biaya dihapus");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const total = (costs || []).reduce((a, c) => a + c.amount, 0);

  return (
    <div data-testid="operational-page">
      <PageHeader title="Biaya Operasional" subtitle="Pengeluaran operasional per cabang">
        <Select value={branch} onValueChange={setBranch}>
          <SelectTrigger data-testid="opcost-branch-filter" className="w-40 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
            <SelectItem value="all">Semua Cabang</SelectItem>
            {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="opcost-month-filter" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-10 bg-[#0F0F12] border-[#1A1A22]" />
        {can(user, "input_biaya_operasional") && (
          <Button data-testid="add-opcost-button" onClick={() => setOpen(true)} disabled={branches.length === 0} className="bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold rounded-md">
            <Plus className="w-4 h-4 mr-1" /> Tambah Biaya
          </Button>
        )}
      </PageHeader>

      {!costs ? (
        <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" />
      ) : costs.length === 0 ? (
        <EmptyState icon={Receipt} title="Belum ada biaya operasional" desc="Catat gaji, sewa, listrik, dan pengeluaran cabang lainnya" />
      ) : (
        <div className="bg-[#0A0A0C] border border-[#1A1A22] rounded-md overflow-x-auto fade-up">
          <table className="w-full text-sm min-w-[700px]" data-testid="opcost-table">
            <thead>
              <tr className="border-b border-[#1A1A22] text-left">
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Tanggal</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Cabang</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Kategori</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500">Keterangan</th>
                <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500 text-right">Jumlah</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {costs.map((c) => (
                <tr key={c.id} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50 transition-colors duration-200">
                  <td className="px-4 py-3 text-zinc-400">{fmtDate(c.date)}</td>
                  <td className="px-4 py-3 text-zinc-300">{c.branch_name}</td>
                  <td className="px-4 py-3 text-zinc-100 font-medium">{c.category}</td>
                  <td className="px-4 py-3 text-zinc-400">{c.description || "-"}
                    {c.created_by_name && <p className="text-[10px] text-zinc-600">dicatat oleh {c.created_by_name}</p>}
                  </td>
                  <td className="px-4 py-3 text-right"><Money value={c.amount} className="text-zinc-100" /></td>
                  <td className="px-4 py-3 text-right">
                    {user?.role === "admin" && (
                      <button data-testid={`delete-opcost-${c.id}`} onClick={() => remove(c.id)} className="text-zinc-600 hover:text-rose-500 transition-colors duration-200">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-[#050505]/50">
                <td colSpan={4} className="px-4 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500">Total</td>
                <td className="px-4 py-3 text-right"><Money value={total} className="text-blue-500 font-semibold" /></td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0A0A0C] border-[#1A1A22] text-zinc-100 max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Tambah Biaya Operasional</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={submit} className="space-y-4" data-testid="opcost-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className={labelCls}>Cabang</Label>
                  {user?.role === "admin_cabang" ? (
                    <div className="h-10 px-3 flex items-center text-sm text-zinc-300 bg-[#050505] border border-[#1A1A22] rounded-md">
                      {branches.find((b) => b.id === form.branch_id)?.name || "Cabang Anda"}
                    </div>
                  ) : (
                    <Select value={form.branch_id} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v, cash_branch_id: v }))}>
                      <SelectTrigger data-testid="opcost-branch-select" className={inputCls}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                        {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className={labelCls}>Tanggal</Label>
                  <Input data-testid="opcost-date-input" type="date" required value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger data-testid="opcost-category-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                    {OP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Sumber Dana (Kas Cabang / Pusat)</Label>
                <Select value={form.cash_branch_id} onValueChange={(v) => setForm((f) => ({ ...f, cash_branch_id: v }))}>
                  <SelectTrigger data-testid="opcost-cash-branch-select" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
                    {user?.role !== "admin_cabang" && <SelectItem value="umum">Kas Umum Pusat (Tanpa Cabang)</SelectItem>}
                    {(user?.role === "admin_cabang" ? branches.filter((b) => b.id === user.branch_id || b.is_pusat) : branches).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Jumlah (Rp)</Label>
                <Input data-testid="opcost-amount-input" type="number" min="1" required value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="2500000" className={inputCls} />
              </div>
              <div className="space-y-2">
                <Label className={labelCls}>Keterangan</Label>
                <Input data-testid="opcost-desc-input" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Keterangan biaya" className={inputCls} />
              </div>
              <Button data-testid="opcost-submit-button" type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-500 text-white shadow-[0_0_12px_rgba(37,99,235,0.25)] font-semibold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Biaya"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
